import { HTMLWidget } from 'apps/admin/widgets.ts'
import type { Product } from 'apps/commerce/types.ts'
import RenderHTML from 'site/components/ui/RenderHTML.tsx'
import c from 'site/sdk/c.ts'
import { SIZE_ORDER } from 'site/utils/sizeOrder.ts'
import { productSelectedSkuID } from 'site/components/product/ProductInfo.tsx'
import { useProductSpecs } from 'site/sdk/useProductVariants.ts'
import { SkuSpecGroup, SkuSpecValue } from 'site/sdk/useProductVariants.ts'

/** @titleBy skuName */
export type SkusText = {
    /**
     * @title Texto dos SKUs
     * @description Texto exibido em cima dos SKUs
     */
    text: HTMLWidget

    /**
     * @title Nome do SKU
     * @description Nome do SKU
     */
    skuName: string
}

interface Props {
    product: Product
    skusText: SkusText[]
}

const MAX_ITEMS_QUANTITY = 8
const ALLOWED_SPECS = ['Tamanho', 'Cor']

function Item({ skuSpec }: { skuSpec: SkuSpecValue }) {
    const { inStock, value, skuID } = skuSpec
    const isSelected = productSelectedSkuID.value === skuID

    return (
        <li>
            <button
                onClick={(e) => {
                    e.preventDefault()
                    productSelectedSkuID.value = skuID
                }}
                class={c(
                    'text-sm text-neutral-1 h-9 px-2 grid place-content-center border transition-all',
                    inStock && !isSelected && 'border-neutral-4 hover:border-neutral-0',
                    !inStock &&
                        'text-neutral-3 overflow-hidden border border-neutral-3 relative after:w-[150%] after:h-px after:-rotate-45 after:origin-right after:top-0 after:right-0 after:absolute after:bg-neutral-3',
                    inStock && isSelected && 'bg-primary-700 text-white',
                    !inStock && isSelected && 'border !border-danger-500',
                )}
            >
                {value}
            </button>
        </li>
    )
}

function Spec({ spec }: { spec: SkuSpecGroup }) {
    // Ordena os tamanhos conforme a ordem definida em SIZE_ORDER
    if (spec.name === 'Tamanho') {
        spec.values.sort((t1, t2) => {
            const index1 = SIZE_ORDER.findIndex((size) => size === t1.value)
            const index2 = SIZE_ORDER.findIndex((size) => size === t2.value)
            return index1 - index2
        })
    }

    const overflow = spec.values.length > MAX_ITEMS_QUANTITY

    return (
        <div class='flex flex-col gap-y-2 relative'>
            <RenderHTML
                html={spec.label}
                class='text-xs'
            />

            <div class='group flex flex-col gap-4'>
                <ul
                    data-overflow={overflow ? '' : undefined}
                    class='flex items-center gap-3 flex-wrap w-full data-[overflow]:h-9 group-has-[input:checked]:h-auto overflow-hidden'
                >
                    {spec.values.map((spec) => {
                        return <Item skuSpec={spec} />
                    })}
                </ul>

                {/* Mostra "ver (+)" apenas quando há overflow */}
                {overflow && (
                    <label class='underline bg-white select-none cursor-pointer font-extrabold text-sm text-neutral-1 whitespace-nowrap'>
                        <input type='checkbox' class='peer hidden' />
                        <span class='peer-checked:hidden'>Ver (+)</span>
                        <span class='hidden peer-checked:inline'>Ver (-)</span>
                    </label>
                )}
            </div>
        </div>
    )
}

export default function VariantSelector({ product, skusText }: Props) {
    const dictionary = skusText.map(({ skuName, text }) => ({
        label: text,
        possibleLabels: [skuName],
    }))

    const specs = useProductSpecs({ product, dictionary })
    const filteredSpecs = specs.filter(({ name }) => ALLOWED_SPECS.includes(name))

    return (
        <div id='sku-selector'>
            {filteredSpecs.map((spec) => {
                return <Spec spec={spec} />
            })}
        </div>
    )
}

// Arquivo de interesse para o rollback da demanda #4561
// Este é um dos arquivos que foram (ou que poderão ser) alterados 
// para realização do rollback